/**
 * useSharing Hook (Web)
 * Thin wrapper around shared useSharing.
 * For a consumer that shows received shares, listens for SSE-driven
 * `parlay-shared-with-you` custom events to refresh the shared-with-me list
 * in real time instead of polling.
 */

import { useAuth } from '@contexts/AuthContext';
import { createHttpClient } from '@parlay-picker/shared/api/httpClient';
import {
  type UseSharingOptions,
  type UseSharingResult,
  useSharing as useSharingShared,
} from '@parlay-picker/shared/hooks';
import { useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config/api';

// Re-export types for existing consumers
export type {
  ShareableUser,
  ShareByMe,
  SharedParlay,
  SharedParlayLeg,
  ShareResult,
  ShareWithMe,
  UseSharingResult,
} from '@parlay-picker/shared/hooks';

/** App's one copy of received shares, passed to the Activity view and its Shared With Me list */
export type ReceivedShares = Pick<
  UseSharingResult,
  | 'sharedWithMe'
  | 'unviewedCount'
  | 'markViewed'
  | 'fetchSharedWithMe'
  | 'sharedWithMeLoading'
  | 'sharedWithMeError'
>;

export function useSharing({
  autoLoadReceived = false,
}: Pick<UseSharingOptions, 'autoLoadReceived'> = {}): UseSharingResult {
  const { authFetch, isAuthenticated } = useAuth();

  const httpClient = useMemo(
    () => createHttpClient({ authFetch, baseUrl: API_BASE_URL }),
    [authFetch],
  );

  const result = useSharingShared({ httpClient, isAuthenticated, autoLoadReceived });

  // A consumer that shows received shares also refreshes them on SSE-driven share notifications.
  // NotificationsProvider dispatches 'parlay-shared-with-you'
  // custom events when an SSE notification of that type arrives.
  useEffect(() => {
    if (!isAuthenticated || !autoLoadReceived) return;

    const handleShareNotification = () => {
      result.fetchSharedWithMe();
    };

    window.addEventListener('parlay-shared-with-you', handleShareNotification);
    return () => {
      window.removeEventListener('parlay-shared-with-you', handleShareNotification);
    };
  }, [isAuthenticated, autoLoadReceived, result.fetchSharedWithMe, result]);

  return result;
}
